import React from "react";
import { motion } from "framer-motion";
import { CheckCircle, Sparkles } from "lucide-react";

const SuccessModal = ({ isOpen, onClose, userName, personality }) => {
  if (!isOpen) return null;

  // Get first name from full name
  const firstName = userName?.split(" ")[0] || "Friend";

  // Get personality-specific content
  const getPersonalityConfig = (personalityType) => {
    const configs = {
      leader: {
        title: `You're all set, ${firstName}! 🚀`,
        message: "Your subscription is active. Time to get answers and move forward with confidence.",
        buttonText: "Start Now",
        gradient: "from-orange-500 via-red-500 to-pink-500",
        iconColor: "text-orange-500",
      },
      thinker: {
        title: `Welcome aboard, ${firstName}! 🧠`,
        message: "Your subscription is active. Ready to explore the connections and dive deep into context.",
        buttonText: "Start Exploring",
        gradient: "from-purple-500 via-blue-500 to-indigo-500",
        iconColor: "text-purple-500",
      },
      promoter: {
        title: `Let's go, ${firstName}! ✨`,
        message: "Your subscription is active. Time to discover amazing insights you'll want to share!",
        buttonText: "Start Discovering",
        gradient: "from-pink-500 via-purple-500 to-blue-500",
        iconColor: "text-pink-500",
      },
      nurturer: {
        title: `Welcome, ${firstName}! 💙`,
        message: "Your subscription is active. Take your time, explore at your own pace—I'm here to help.",
        buttonText: "Begin Journey",
        gradient: "from-green-500 via-teal-500 to-emerald-500",
        iconColor: "text-green-500",
      },
    };
    return configs[personalityType] || configs.nurturer;
  };

  const config = getPersonalityConfig(personality);

  const containerVariants = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.3,
        ease: "easeOut",
      },
    },
  };

  const checkmarkVariants = {
    hidden: { scale: 0, rotate: -180 },
    visible: {
      scale: 1,
      rotate: 0,
      transition: {
        type: "spring",
        stiffness: 200,
        damping: 15,
        delay: 0.1,
      },
    },
  };

  const sparkleVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        delay: 0.3,
      },
    },
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black bg-opacity-60">
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="relative w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden">
        {/* Decorative gradient bar */}
        <div className={`h-2 bg-gradient-to-r ${config.gradient}`} />

        {/* Content */}
        <div className="p-8 text-center">
          {/* Success Icon with Animation */}
          <motion.div
            variants={checkmarkVariants}
            initial="hidden"
            animate="visible"
            className="flex justify-center mb-6">
            <div className={`relative`}>
              <CheckCircle size={80} className={`${config.iconColor}`} strokeWidth={1.5} />
              {/* Sparkles around the checkmark */}
              <motion.div
                variants={sparkleVariants}
                initial="hidden"
                animate="visible"
                className="absolute -top-2 -right-2">
                <Sparkles size={24} className={config.iconColor} />
              </motion.div>
            </div>
          </motion.div>

          {/* Title */}
          <motion.h2
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-3xl font-bold text-gray-900 mb-4">
            {config.title}
          </motion.h2>

          {/* Message */}
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-lg text-gray-600 mb-8 leading-relaxed">
            {config.message}
          </motion.p>

          {/* CTA Button */}
          <motion.button
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={onClose}
            className={`w-full bg-gradient-to-r ${config.gradient} text-white font-semibold text-lg py-4 px-8 rounded-full shadow-lg hover:shadow-xl transition-all`}>
            {config.buttonText}
          </motion.button>
        </div>
      </motion.div>
    </div>
  );
};

export default SuccessModal;
